import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import Loader from 'components/loader';
import Modal from 'components/modal';
import request from 'library/@e5p-request';
import mutations from 'library/mutation';

import {
  getIntegrationCounts,
  getExpirationTime,
  getRemainingDays,
  getSafeAuthorizationUrl,
  groupOAuthAccountsByCompany,
  INTEGRATION_STATUS,
} from './helpers';
import styles from './styles.scss';

const { getOAuthCompanyAccounts } = request;
const { createOAuthAuthorizationUrl } = mutations;

const EXPIRING_SOON_DAYS = 14;
const STATUS_REFRESH_INTERVAL_MILLISECONDS = 30000;
const SLOW_AUTHORIZATION_NOTICE_MILLISECONDS = 15000;

const canCreateAuthorization = (account) =>
  Boolean(
    account &&
      typeof account.vkn === 'string' &&
      account.vkn.trim() &&
      typeof account.uid === 'string' &&
      account.uid.trim() &&
      Number.isInteger(account.bankCode),
  );

const getStatusMessage = (intl, status) => {
  const statusMessages = {
    [INTEGRATION_STATUS.DISCONNECTED]: {
      id: 'bankIntegrations.status.disconnected',
      defaultMessage: 'Bağlı Değil',
    },
    [INTEGRATION_STATUS.EXPIRED]: {
      id: 'bankIntegrations.status.expired',
      defaultMessage: 'Süresi Dolmuş',
    },
    [INTEGRATION_STATUS.ACTIVE]: {
      id: 'bankIntegrations.status.active',
      defaultMessage: 'Aktif',
    },
  };

  return intl.formatMessage(statusMessages[status]);
};

const getRemainingTimeMessage = (intl, account) => {
  const remainingDays = getRemainingDays(account);

  if (account.integrationStatus === INTEGRATION_STATUS.DISCONNECTED) {
    return intl.formatMessage({
      id: 'bankIntegrations.remaining.notAuthorized',
      defaultMessage: 'Yetkilendirme bekliyor',
    });
  }

  if (account.integrationStatus === INTEGRATION_STATUS.EXPIRED) {
    if (remainingDays !== null && remainingDays < 0) {
      return intl.formatMessage(
        {
          id: 'bankIntegrations.remaining.expiredDaysAgo',
          defaultMessage: '{days} gün önce doldu',
        },
        { days: Math.abs(remainingDays) },
      );
    }

    return intl.formatMessage({
      id: 'bankIntegrations.remaining.expired',
      defaultMessage: 'Süresi doldu',
    });
  }

  if (remainingDays === null) {
    return intl.formatMessage({
      id: 'bankIntegrations.notAvailable',
      defaultMessage: 'Yok',
    });
  }

  if (remainingDays <= 0) {
    return intl.formatMessage({
      id: 'bankIntegrations.remaining.withinDay',
      defaultMessage: '24 saatten az kaldı',
    });
  }

  return intl.formatMessage(
    {
      id: 'bankIntegrations.remaining.days',
      defaultMessage: '{days} gün kaldı',
    },
    { days: remainingDays },
  );
};

const CompanyIntegrationGroup = ({ company, companyIndex, intl, onSelectAccount, theme }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const companyId = encodeURIComponent(company.key || companyIndex);
  const contentId = `bank-integrations-company-${companyId}`;
  const titleId = `bank-integrations-company-title-${companyId}`;
  const counts = getIntegrationCounts(company.accounts);

  return (
    <section className={cx(styles['company-group'], styles[`company-group-${theme}`])} aria-labelledby={titleId}>
      <span id={titleId} className={styles['visually-hidden']}>
        {company.companyName}, <FormattedMessage id="bankIntegrations.company.vkn" defaultMessage="VKN" />:{' '}
        {company.vkn}
      </span>
      <button
        type="button"
        className={cx(styles['company-header'], styles[`company-header-${theme}`])}
        aria-controls={contentId}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((currentValue) => !currentValue)}>
        <span className={styles['company-identity']}>
          <span className={styles['company-avatar']} aria-hidden="true">
            {(company.companyName || '?').charAt(0).toLocaleUpperCase('tr-TR')}
          </span>
          <span className={styles['company-details']}>
            <strong>{company.companyName}</strong>
            <span>
              <FormattedMessage id="bankIntegrations.company.vkn" defaultMessage="VKN" />: {company.vkn}
            </span>
          </span>
        </span>
        <span className={styles['company-overview']}>
          <span className={styles['company-bank-count']}>
            <FormattedMessage
              id="bankIntegrations.company.bankCount"
              defaultMessage="{count} banka"
              values={{ count: company.accounts.length }}
            />
          </span>
          {(counts[INTEGRATION_STATUS.DISCONNECTED] > 0 || counts[INTEGRATION_STATUS.EXPIRED] > 0) && (
            <span className={styles['company-attention-count']}>
              <FormattedMessage
                id="bankIntegrations.company.actionRequired"
                defaultMessage="{count} işlem bekliyor"
                values={{
                  count: counts[INTEGRATION_STATUS.DISCONNECTED] + counts[INTEGRATION_STATUS.EXPIRED],
                }}
              />
            </span>
          )}
          <span className={cx(styles.chevron, { [styles['chevron-expanded']]: isExpanded })} aria-hidden="true">
            ⌄
          </span>
        </span>
      </button>

      {isExpanded && (
        <div id={contentId} className={styles['table-scroll']}>
          <table className={styles.table} aria-labelledby={titleId}>
            <thead>
              <tr>
                <th scope="col">
                  <FormattedMessage id="bankIntegrations.table.bank" defaultMessage="Banka" />
                </th>
                <th scope="col">
                  <FormattedMessage id="bankIntegrations.table.status" defaultMessage="Durum" />
                </th>
                <th scope="col">
                  <FormattedMessage id="bankIntegrations.table.remaining" defaultMessage="Kalan Süre" />
                </th>
                <th scope="col">
                  <FormattedMessage id="bankIntegrations.table.expiration" defaultMessage="Geçerlilik Tarihi" />
                </th>
                <th scope="col" className={styles['action-heading']}>
                  <FormattedMessage id="bankIntegrations.table.action" defaultMessage="İşlem" />
                </th>
              </tr>
            </thead>
            <tbody>
              {company.accounts.map((account, accountIndex) => {
                const remainingDays = getRemainingDays(account);
                const expirationTime = getExpirationTime(account);
                const isExpiringSoon =
                  account.integrationStatus === INTEGRATION_STATUS.ACTIVE &&
                  remainingDays !== null &&
                  remainingDays <= EXPIRING_SOON_DAYS;
                const isActive = account.integrationStatus === INTEGRATION_STATUS.ACTIVE;
                const isAuthorizationAvailable = canCreateAuthorization(account);
                const accountKey = `${company.vkn}-${account.bankCode}-${account.uid || accountIndex}`;
                const actionId = `bank-integration-action-${encodeURIComponent(company.key)}-${encodeURIComponent(
                  account.bankCode,
                )}-${encodeURIComponent(account.uid || accountIndex)}`;
                const actionValues = {
                  bankName: account.bankName || '-',
                  companyName: company.companyName,
                  vkn: company.vkn,
                };
                const actionText = isActive
                  ? intl.formatMessage({ id: 'bankIntegrations.action.active', defaultMessage: 'Aktif' })
                  : !isAuthorizationAvailable
                  ? intl.formatMessage({
                      id: 'bankIntegrations.action.unavailable',
                      defaultMessage: 'Kullanılamıyor',
                    })
                  : intl.formatMessage({
                      id:
                        account.integrationStatus === INTEGRATION_STATUS.EXPIRED
                          ? 'bankIntegrations.action.reconnect'
                          : 'bankIntegrations.action.connect',
                      defaultMessage:
                        account.integrationStatus === INTEGRATION_STATUS.EXPIRED ? 'Yeniden Bağlan' : 'Bağlan',
                    });
                const actionAriaLabel = isActive
                  ? intl.formatMessage(
                      {
                        id: 'bankIntegrations.action.activeLabel',
                        defaultMessage: '{companyName} (VKN: {vkn}) için {bankName} bağlantısı aktif',
                      },
                      actionValues,
                    )
                  : !isAuthorizationAvailable
                  ? intl.formatMessage(
                      {
                        id: 'bankIntegrations.action.unavailableLabel',
                        defaultMessage: '{companyName} (VKN: {vkn}) için {bankName} bağlantısı kullanılamıyor',
                      },
                      actionValues,
                    )
                  : intl.formatMessage(
                      {
                        id:
                          account.integrationStatus === INTEGRATION_STATUS.EXPIRED
                            ? 'bankIntegrations.action.reconnectLabel'
                            : 'bankIntegrations.action.connectLabel',
                        defaultMessage:
                          account.integrationStatus === INTEGRATION_STATUS.EXPIRED
                            ? '{companyName} (VKN: {vkn}) için {bankName} bağlantısını yenile'
                            : '{companyName} (VKN: {vkn}) için {bankName} bankasına bağlan',
                      },
                      actionValues,
                    );

                return (
                  <tr key={accountKey}>
                    <td data-label={intl.formatMessage({ id: 'bankIntegrations.table.bank', defaultMessage: 'Banka' })}>
                      <span className={styles['bank-name']}>{account.bankName || '-'}</span>
                      {account.bankCode != null && (
                        <span className={styles['bank-code']}>
                          <FormattedMessage
                            id="bankIntegrations.bank.code"
                            defaultMessage="Banka kodu: {code}"
                            values={{ code: account.bankCode }}
                          />
                        </span>
                      )}
                    </td>
                    <td
                      data-label={intl.formatMessage({
                        id: 'bankIntegrations.table.status',
                        defaultMessage: 'Durum',
                      })}>
                      <span className={cx(styles['status-badge'], styles[`status-badge-${account.integrationStatus}`])}>
                        <span className={styles['status-dot']} aria-hidden="true" />
                        {getStatusMessage(intl, account.integrationStatus)}
                      </span>
                    </td>
                    <td
                      data-label={intl.formatMessage({
                        id: 'bankIntegrations.table.remaining',
                        defaultMessage: 'Kalan Süre',
                      })}>
                      <span
                        className={cx(styles['remaining-time'], {
                          [styles['remaining-time-warning']]: isExpiringSoon,
                        })}>
                        {getRemainingTimeMessage(intl, account)}
                      </span>
                      {isExpiringSoon && (
                        <span className={styles['expiring-soon']}>
                          <FormattedMessage id="bankIntegrations.remaining.soon" defaultMessage="Yakında dolacak" />
                        </span>
                      )}
                    </td>
                    <td
                      data-label={intl.formatMessage({
                        id: 'bankIntegrations.table.expiration',
                        defaultMessage: 'Geçerlilik Tarihi',
                      })}>
                      {expirationTime !== null ? (
                        <time dateTime={account.refreshTokenExpiresAtUtc} className={styles['expiration-date']}>
                          {intl.formatDate(expirationTime, {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            timeZone: 'UTC',
                          })}
                        </time>
                      ) : (
                        <span
                          aria-label={intl.formatMessage({
                            id: 'bankIntegrations.notAvailable',
                            defaultMessage: 'Yok',
                          })}>
                          —
                        </span>
                      )}
                    </td>
                    <td
                      data-label={intl.formatMessage({
                        id: 'bankIntegrations.table.action',
                        defaultMessage: 'İşlem',
                      })}
                      className={styles['action-cell']}>
                      <button
                        id={actionId}
                        type="button"
                        aria-label={actionAriaLabel}
                        disabled={!isAuthorizationAvailable}
                        title={
                          !isAuthorizationAvailable
                            ? intl.formatMessage({
                                id: 'bankIntegrations.action.unavailableDescription',
                                defaultMessage: 'Bu banka için bağlantı bilgileri eksik.',
                              })
                            : undefined
                        }
                        className={cx(styles['row-action'], {
                          [styles['row-action-disabled']]: !isAuthorizationAvailable,
                        })}
                        onClick={() => onSelectAccount(account, actionId)}>
                        {actionText}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const BankIntegrationsModal = ({ onClose, returnFocusRef, theme }) => {
  const intl = useIntl();
  const latestRequestId = useRef(0);
  const latestAuthorizationAttemptId = useRef(0);
  const authorizationAbortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const confirmationCancelButtonRef = useRef(null);
  const listContentRef = useRef(null);
  const previousSelectedAccountRef = useRef(null);
  const selectedActionIdRef = useRef(null);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isAuthorizationSlow, setIsAuthorizationSlow] = useState(false);
  const [authorizationError, setAuthorizationError] = useState(false);
  const [statusEvaluationTime, setStatusEvaluationTime] = useState(Date.now());

  const loadAccounts = useCallback(async () => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    setIsLoading(true);
    setLoadError(false);

    try {
      const result = await getOAuthCompanyAccounts();
      if (latestRequestId.current !== requestId) return;
      setAccounts(Array.isArray(result) ? result : []);
    } catch (error) {
      if (latestRequestId.current !== requestId) return;
      setAccounts([]);
      setLoadError(true);
    } finally {
      if (latestRequestId.current === requestId) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadAccounts();
    return () => {
      isMountedRef.current = false;
      latestRequestId.current += 1;
      latestAuthorizationAttemptId.current += 1;
      if (authorizationAbortControllerRef.current) authorizationAbortControllerRef.current.abort();
    };
  }, [loadAccounts]);

  useEffect(() => {
    const intervalId = setInterval(() => setStatusEvaluationTime(Date.now()), STATUS_REFRESH_INTERVAL_MILLISECONDS);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!isAuthorizing) return undefined;

    const timeoutId = setTimeout(() => setIsAuthorizationSlow(true), SLOW_AUTHORIZATION_NOTICE_MILLISECONDS);
    return () => clearTimeout(timeoutId);
  }, [isAuthorizing]);

  useEffect(() => {
    if (selectedAccount && confirmationCancelButtonRef.current) {
      confirmationCancelButtonRef.current.focus();
    } else if (previousSelectedAccountRef.current) {
      const selectedAction = document.getElementById(selectedActionIdRef.current);
      if (selectedAction) {
        selectedAction.focus();
      } else if (listContentRef.current) {
        listContentRef.current.focus();
      }
    }

    previousSelectedAccountRef.current = selectedAccount;
  }, [selectedAccount]);

  const companies = useMemo(() => groupOAuthAccountsByCompany(accounts, statusEvaluationTime), [
    accounts,
    statusEvaluationTime,
  ]);
  const sortedAccounts = useMemo(
    () => companies.reduce((allAccounts, company) => allAccounts.concat(company.accounts), []),
    [companies],
  );
  const counts = useMemo(() => getIntegrationCounts(sortedAccounts), [sortedAccounts]);

  const handleClose = () => {
    if (selectedAccount) {
      latestAuthorizationAttemptId.current += 1;
      if (authorizationAbortControllerRef.current) {
        authorizationAbortControllerRef.current.abort();
        authorizationAbortControllerRef.current = null;
      }
      setSelectedAccount(null);
      setIsAuthorizing(false);
      setIsAuthorizationSlow(false);
      setAuthorizationError(false);
      return;
    }
    onClose();
  };

  const handleSelectAccount = (account, actionId) => {
    if (!canCreateAuthorization(account)) return;
    selectedActionIdRef.current = actionId;
    setAuthorizationError(false);
    setIsAuthorizationSlow(false);
    setSelectedAccount(account);
  };

  const handleAuthorization = async () => {
    if (!canCreateAuthorization(selectedAccount) || isAuthorizing) return;
    const authorizationAttemptId = latestAuthorizationAttemptId.current + 1;
    latestAuthorizationAttemptId.current = authorizationAttemptId;
    setIsAuthorizing(true);
    setIsAuthorizationSlow(false);
    setAuthorizationError(false);

    const abortController = typeof AbortController === 'function' ? new AbortController() : null;
    authorizationAbortControllerRef.current = abortController;

    try {
      const authorization = await createOAuthAuthorizationUrl(
        {
          vkn: selectedAccount.vkn.trim(),
          bankCode: selectedAccount.bankCode,
          uid: selectedAccount.uid.trim(),
        },
        { signal: abortController && abortController.signal },
      );
      if (!isMountedRef.current || latestAuthorizationAttemptId.current !== authorizationAttemptId) return;
      const authorizationUrl = getSafeAuthorizationUrl(authorization, Date.now());
      window.location.assign(authorizationUrl);
    } catch (error) {
      if (!isMountedRef.current || latestAuthorizationAttemptId.current !== authorizationAttemptId) return;
      setAuthorizationError(true);
      setIsAuthorizing(false);
      setIsAuthorizationSlow(false);
    } finally {
      if (authorizationAbortControllerRef.current === abortController) {
        authorizationAbortControllerRef.current = null;
      }
    }
  };

  const renderSummary = () => (
    <div
      className={styles.summary}
      aria-label={intl.formatMessage({ id: 'bankIntegrations.summary', defaultMessage: 'Bağlantı özeti' })}>
      <div className={cx(styles['summary-item'], styles['summary-item-active'])}>
        <span>{counts[INTEGRATION_STATUS.ACTIVE]}</span>
        <FormattedMessage id="bankIntegrations.summary.active" defaultMessage="Aktif" />
      </div>
      <div className={cx(styles['summary-item'], styles['summary-item-expired'])}>
        <span>{counts[INTEGRATION_STATUS.EXPIRED]}</span>
        <FormattedMessage id="bankIntegrations.summary.expired" defaultMessage="Süresi dolmuş" />
      </div>
      <div className={cx(styles['summary-item'], styles['summary-item-disconnected'])}>
        <span>{counts[INTEGRATION_STATUS.DISCONNECTED]}</span>
        <FormattedMessage id="bankIntegrations.summary.disconnected" defaultMessage="Bağlı değil" />
      </div>
    </div>
  );

  const renderListContent = () => {
    if (isLoading) {
      return (
        <div className={styles['state-panel']} role="status" aria-live="polite">
          <Loader />
          <p>
            <FormattedMessage id="bankIntegrations.loading" defaultMessage="Banka bağlantıları yükleniyor…" />
          </p>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className={styles['state-panel']} role="alert">
          <span className={cx(styles['state-icon'], styles['state-icon-error'])} aria-hidden="true">
            !
          </span>
          <h3>
            <FormattedMessage id="bankIntegrations.error.title" defaultMessage="Bağlantılar yüklenemedi" />
          </h3>
          <p>
            <FormattedMessage
              id="bankIntegrations.error.description"
              defaultMessage="Bilgileri şu anda alamıyoruz. Lütfen bağlantınızı kontrol edip tekrar deneyin."
            />
          </p>
          <button type="button" className={styles['primary-button']} onClick={loadAccounts}>
            <FormattedMessage id="bankIntegrations.action.retry" defaultMessage="Tekrar Dene" />
          </button>
        </div>
      );
    }

    if (companies.length === 0) {
      return (
        <div className={styles['state-panel']} role="status">
          <span className={styles['state-icon']} aria-hidden="true">
            —
          </span>
          <h3>
            <FormattedMessage id="bankIntegrations.empty.title" defaultMessage="Banka kaydı bulunamadı" />
          </h3>
          <p>
            <FormattedMessage
              id="bankIntegrations.empty.description"
              defaultMessage="Şirketlerinize tanımlı bir banka entegrasyonu henüz bulunmuyor."
            />
          </p>
        </div>
      );
    }

    return (
      <div ref={listContentRef} tabIndex={-1} className={styles['list-content']}>
        <div className={styles['modal-introduction']}>
          <p>
            <FormattedMessage
              id="bankIntegrations.description"
              defaultMessage="Şirketlerinizin banka bağlantılarını, kalan yetkilendirme sürelerini ve gerekli işlemleri buradan takip edebilirsiniz."
            />
          </p>
          {renderSummary()}
        </div>
        <div className={styles['company-list']}>
          {companies.map((company, companyIndex) => (
            <CompanyIntegrationGroup
              key={company.key}
              company={company}
              companyIndex={companyIndex}
              intl={intl}
              onSelectAccount={handleSelectAccount}
              theme={theme}
            />
          ))}
        </div>
        <p className={styles['freshness-note']}>
          <span aria-hidden="true">↻</span>{' '}
          <FormattedMessage
            id="bankIntegrations.freshness"
            defaultMessage="Bağlantı bilgileri bu pencere her açıldığında güncellenir."
          />
        </p>
      </div>
    );
  };

  const renderConfirmation = () => {
    const isReconnect = selectedAccount.integrationStatus === INTEGRATION_STATUS.EXPIRED;

    return (
      <div className={styles.confirmation}>
        <span className={styles['confirmation-icon']} aria-hidden="true">
          ↗
        </span>
        <h2>
          {intl.formatMessage(
            {
              id: isReconnect
                ? 'bankIntegrations.confirmation.reconnectTitle'
                : 'bankIntegrations.confirmation.connectTitle',
              defaultMessage: isReconnect ? '{bankName} bağlantısını yenile' : '{bankName} bankasına bağlan',
            },
            { bankName: selectedAccount.bankName },
          )}
        </h2>
        <p>
          {intl.formatMessage(
            {
              id: isReconnect
                ? 'bankIntegrations.confirmation.reconnectDescription'
                : 'bankIntegrations.confirmation.connectDescription',
              defaultMessage: isReconnect
                ? '{companyName} (VKN: {vkn}) için {bankName} banka bağlantısını yeniden yetkilendirmek üzere bankanın sayfasına yönlendirileceksiniz.'
                : '{companyName} (VKN: {vkn}) için {bankName} banka bağlantısını yetkilendirmek üzere bankanın sayfasına yönlendirileceksiniz.',
            },
            {
              companyName: selectedAccount.companyName,
              bankName: selectedAccount.bankName,
              vkn: selectedAccount.vkn,
            },
          )}
        </p>
        <div className={styles['security-note']}>
          <span aria-hidden="true">ⓘ</span>
          <FormattedMessage
            id="bankIntegrations.confirmation.security"
            defaultMessage="İşleme devam ettiğinizde bu uygulamadan ayrılacak ve bankanın güvenli yetkilendirme sayfasına geçeceksiniz."
          />
        </div>
        {authorizationError && (
          <div className={styles['authorization-error']} role="alert">
            <FormattedMessage
              id="bankIntegrations.authorization.error"
              defaultMessage="Güvenli yönlendirme bağlantısı oluşturulamadı. Lütfen tekrar deneyin."
            />
          </div>
        )}
        {isAuthorizationSlow && (
          <div className={styles['authorization-slow']} role="status" aria-live="polite">
            <span aria-hidden="true">!</span>
            <FormattedMessage
              id="bankIntegrations.authorization.slow"
              defaultMessage="İşlem beklenenden uzun sürüyor. Dilerseniz İptal ile güvenle listeye dönebilirsiniz."
            />
          </div>
        )}
        <div className={styles['confirmation-actions']}>
          <button
            ref={confirmationCancelButtonRef}
            type="button"
            className={styles['secondary-button']}
            onClick={handleClose}>
            <FormattedMessage id="bankIntegrations.action.cancel" defaultMessage="İptal" />
          </button>
          <button
            type="button"
            className={styles['primary-button']}
            disabled={isAuthorizing}
            onClick={handleAuthorization}>
            {isAuthorizing ? (
              <FormattedMessage id="bankIntegrations.action.redirecting" defaultMessage="Yönlendiriliyor…" />
            ) : (
              <FormattedMessage id="bankIntegrations.action.continue" defaultMessage="Devam Et" />
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      theme={theme}
      hide={handleClose}
      title={
        selectedAccount
          ? intl.formatMessage({ id: 'bankIntegrations.confirmation.header', defaultMessage: 'Yönlendirme Onayı' })
          : intl.formatMessage({ id: 'bankIntegrations.title', defaultMessage: 'Banka Entegrasyonları' })
      }
      ariaLabel={intl.formatMessage({ id: 'bankIntegrations.title', defaultMessage: 'Banka Entegrasyonları' })}
      closeLabel={intl.formatMessage({ id: 'bankIntegrations.action.close', defaultMessage: 'Kapat' })}
      closeOnClickOutside
      closeOnEscape
      showCloseIcon
      trapFocus
      returnFocusRef={returnFocusRef}
      className={cx(
        styles['bank-integrations-modal'],
        {
          [styles['bank-integrations-modal-confirmation']]: selectedAccount,
        },
        styles[`bank-integrations-modal-${theme}`],
      )}
      headerClassName={styles['modal-header']}
      titleClassName={styles['modal-title']}
      contentClassName={styles['modal-content']}>
      {selectedAccount ? renderConfirmation() : renderListContent()}
    </Modal>
  );
};

export default BankIntegrationsModal;
